import React, { useState } from 'react';
import { useCallStore, CallRecord } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { setCallbackStatus, setCallbackNote } from '../api/status';
import { PERIOD_LABELS } from './admin/shared';

// Kompakte Live-Liste für die Mini-Ansicht (PiP-Fenster bzw. #/mini-Popup).
// Teilt sich Store, WebSocket-Push und Status-Sync mit der Hauptansicht.

const ANSWERED = new Set(['SUCCESS', 'TRANSFERRED']);

function timePart(value: unknown): string {
  const s = String(value ?? '');
  return /^\d{4}-\d{2}-\d{2} /.test(s) ? s.slice(11, 16) : s;
}

// Notiz-Zeile mit Inline-Bearbeitung (wie NoteCell der Haupttabelle, nur kompakt).
// Zeilen ohne Notiz zeigen beim Ueberfahren ein dezentes "+ Notiz".
function MiniNote({ call, readOnly }: { call: CallRecord; readOnly: boolean }) {
  const updateStatus = useCallStore((s) => s.updateStatus);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(call.callbackNote);

  async function save() {
    setEditing(false);
    if (draft === call.callbackNote) return;
    updateStatus(call.sessionIdentifier, { callbackNote: draft, callbackAt: new Date().toISOString() });
    try {
      await setCallbackNote(call.sessionIdentifier, draft);
    } catch {
      updateStatus(call.sessionIdentifier, { callbackNote: call.callbackNote });
    }
  }

  if (editing) {
    return (
      <div className="py-1" style={{ paddingLeft: 62 }}>
        <input
          className="form-control form-control-sm"
          style={{ fontSize: '0.74rem', padding: '2px 8px' }}
          value={draft}
          autoFocus
          maxLength={200}
          placeholder="Notiz…"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') { setDraft(call.callbackNote); setEditing(false); }
          }}
        />
      </div>
    );
  }

  if (call.callbackNote) {
    return (
      <div
        className={`text-muted text-truncate ${readOnly ? '' : 'mini-note-click'}`}
        style={{ fontSize: '0.72rem', paddingLeft: 62, lineHeight: 1.3 }}
        title={readOnly
          ? `${call.callbackNote}${call.callbackBy ? ` — ${call.callbackBy}` : ''}`
          : `${call.callbackNote}${call.callbackBy ? ` — ${call.callbackBy}` : ''}\n(Klicken zum Bearbeiten)`}
        onClick={() => { if (!readOnly) { setDraft(call.callbackNote); setEditing(true); } }}
      >
        📝 {call.callbackNote}
        {call.callbackBy && <span style={{ opacity: 0.6 }}> — {call.callbackBy}</span>}
      </div>
    );
  }

  if (readOnly) return null;
  return (
    <div
      className="mini-note-add text-muted"
      style={{ fontSize: '0.7rem', paddingLeft: 62, cursor: 'pointer', lineHeight: 1.3 }}
      onClick={() => { setDraft(''); setEditing(true); }}
    >
      + Notiz
    </div>
  );
}

export default function MiniCallList() {
  const { calls, filter, updateStatus, loading } = useCallStore();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const companyName = useAuthStore((s) => s.company?.name);
  const readOnly = isAdmin && !!filter.viewAsCompanyId;
  const wsConnected = useCallStore((s) => s.wsConnected);

  // Notizen als zweite Zeile unter dem Anruf — abschaltbar, Wahl wird gemerkt.
  const [showNotes, setShowNotes] = useState<boolean>(() => {
    try { return localStorage.getItem('eves_mini_notes') !== 'false'; } catch { return true; }
  });
  function toggleNotes() {
    setShowNotes((v) => {
      try { localStorage.setItem('eves_mini_notes', String(!v)); } catch { /* ignore */ }
      return !v;
    });
  }

  async function toggle(call: CallRecord) {
    if (readOnly) return;
    const next = !call.callbackDone;
    updateStatus(call.sessionIdentifier, { callbackDone: next, callbackAt: new Date().toISOString() });
    try {
      await setCallbackStatus(call.sessionIdentifier, next);
    } catch {
      updateStatus(call.sessionIdentifier, { callbackDone: call.callbackDone, callbackAt: call.callbackAt });
    }
  }

  return (
    <div className="d-flex flex-column bg-body" style={{ height: '100vh', fontSize: '0.8rem' }}>
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-body sticky-top">
        <span className={wsConnected ? 'live-dot' : 'live-dot live-dot-off'} aria-hidden />
        <span className="fw-semibold text-truncate">{companyName ?? 'Anrufe'} · {PERIOD_LABELS[filter.period] ?? filter.period}</span>
        <span className="text-muted">{calls.length}{filter.missedOnly ? ' verpasste' : ''}</span>
        {loading && <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />}
        <button
          className={`btn btn-sm py-0 px-1 ms-auto ${showNotes ? 'header-icon-active' : 'header-icon-btn'}`}
          style={{ fontSize: '0.78rem' }}
          title={showNotes ? 'Notizen ausblenden' : 'Notizen einblenden'}
          onClick={toggleNotes}
        >
          📝
        </button>
      </div>

      <div className="flex-grow-1 overflow-auto">
        {calls.length === 0 ? (
          <div className="text-center text-muted py-5 small">Keine Anrufe im Zeitraum.</div>
        ) : calls.map((call) => {
          const missed = !ANSWERED.has(String(call['result'] ?? '').toUpperCase());
          const from = String(call['inboundSourceDialString'] ?? '') || '—';
          const to = String(call['outboundDestinationDialString'] ?? call['inboundDestinationDialString'] ?? '') || '—';
          return (
            <div
              key={call.sessionIdentifier}
              className={`px-3 py-1 border-bottom mini-row ${call.callbackDone ? 'mini-row-done' : ''}`}
            >
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted text-nowrap" style={{ width: 38 }}>{timePart(call['startDateTime'])}</span>
                <span
                  aria-hidden
                  title={String(call['result'] ?? '')}
                  style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: missed ? 'var(--bs-danger)' : 'var(--bs-success)',
                  }}
                />
                <span className="text-truncate flex-grow-1" title={`${from} → ${to}${call.callbackNote ? `\nNotiz: ${call.callbackNote}` : ''}`}>
                  <span className="font-monospace">{from}</span>
                  <span className="text-muted"> → </span>
                  <span className="font-monospace">{to}</span>
                  {!showNotes && call.callbackNote && <span className="text-muted"> 📝</span>}
                </span>
                <button
                  className={`btn btn-sm py-0 px-1 ${call.callbackDone ? 'btn-success' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.72rem', minWidth: 26 }}
                  title={readOnly ? 'Nur-Lese-Ansicht' : call.callbackDone ? 'Als ausstehend markieren' : 'Rückruf erledigt'}
                  disabled={readOnly}
                  onClick={() => toggle(call)}
                >
                  {call.callbackDone ? '✓' : '○'}
                </button>
              </div>
              {showNotes && <MiniNote key={`${call.sessionIdentifier}-note`} call={call} readOnly={readOnly} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
